import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock, GridCards, BulletList, NumberedList, Callout } from "@/components/InnerBlock";
import { FaqSection } from "@/components/FaqSection";
import { RelatedTiles } from "@/components/RelatedTiles";
import { InnerCTA } from "@/components/InnerCTA";
import { Placeholder } from "@/components/Placeholder";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { WA_SERVICE_LABEL } from "@/lib/whatsapp";

const serviceWa = (pos) => (
  <WhatsAppButton variant="whatsapp" size="lg" label={WA_SERVICE_LABEL.en} source={`service_${pos}`} testId={`service-whatsapp-${pos}`} />
);

// Standard 5-step "how we work" process used by every service page.
const STANDARD_PROCESS = [
  "Thirty-minute scoping call with an engineer. Not a sales team.",
  "Written one-page brief: scope, risks, cost band and timeline.",
  "Fixed-price design and architecture sprint.",
  "Two-week build sprints. Working software every fortnight.",
  "Handover, run and support under a written SLA.",
];

/**
 * Generic inner page renderer. Content is a list of typed section objects,
 * so every service / industry / solution page can share the same layout
 * but expose its own copy verbatim.
 *
 *   { type: 'block', tint, heading, subheading, body, bullets, items, columns }
 *   { type: 'callout', text, tint }
 *   { type: 'grid',   heading, items, columns }
 *   { type: 'bullets', heading, items }
 *   { type: 'numbered', heading, items }
 *   { type: 'stack',  heading, items }  // pill-style tag list
 *   { type: 'faq',    items }
 *   { type: 'process', items }          // uses standard process if items omitted
 *   { type: 'placeholder', id, width, height, caption }
 */
export function InnerPage({ eyebrow, h1, intro, breadcrumbs, meta, sections, related, cta, serviceWhatsApp = false }) {
  const ctaExtra = serviceWhatsApp ? serviceWa("bottom") : null;
  return (
    <div data-testid="inner-page">
      <Seo title={meta?.title} description={meta?.description} />
      <PageHero
        eyebrow={eyebrow}
        h1={h1}
        intro={intro}
        breadcrumbs={breadcrumbs}
        actions={
          <>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep"
              data-testid="page-hero-cta"
            >
              Book a Free Scoping Call <ArrowRight className="h-4 w-4" />
            </Link>
            {serviceWhatsApp ? serviceWa("top") : null}
          </>
        }
      />

      {sections?.map((s, i) => renderSection(s, i))}

      {related?.length ? <RelatedTiles items={related} /> : null}

      {cta ? <InnerCTA heading={cta.heading} body={cta.body} extra={ctaExtra} /> : <InnerCTA heading="Ready to talk?" body="Book a thirty-minute call. An engineer will listen, ask the right questions, and send you a one-page brief." extra={ctaExtra} />}
    </div>
  );
}

function renderSection(s, i) {
  const tint = i % 2 === 0 ? "white" : "cream";
  const activeTint = s.tint || tint;
  // Body copy on dark blocks needs a light colour or it renders black on
  // dark green (see AI Lab "Featured offer"). Everything on `white`/`cream`
  // keeps the existing ink colour.
  const isDark = activeTint === "deep";
  const bodyText = isDark ? "text-white/85" : "text-wafa-ink/85";
  switch (s.type) {
    case "block":
      return (
        <InnerBlock key={i} tint={activeTint} eyebrow={s.eyebrow} heading={s.heading} subheading={s.subheading}>
          {s.callout ? <div className="mb-8"><Callout>{s.callout}</Callout></div> : null}
          {s.body ? <p className={`max-w-3xl text-base leading-relaxed ${bodyText} lg:text-lg`}>{s.body}</p> : null}
          {s.paragraphs ? (
            <div className={`max-w-3xl space-y-4 text-base leading-relaxed ${bodyText} lg:text-lg`}>
              {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
            </div>
          ) : null}
          {s.bullets ? <div className="mt-6"><BulletList items={s.bullets} /></div> : null}
          {s.items ? <div className="mt-8"><GridCards items={s.items} columns={s.columns || 3} /></div> : null}
        </InnerBlock>
      );
    case "callout":
      return (
        <InnerBlock key={i} tint={activeTint} heading={s.heading}>
          <Callout>{s.text}</Callout>
        </InnerBlock>
      );
    case "grid":
      return (
        <InnerBlock key={i} tint={activeTint} eyebrow={s.eyebrow} heading={s.heading} subheading={s.subheading}>
          <GridCards items={s.items} columns={s.columns || 3} />
        </InnerBlock>
      );
    case "bullets":
      return (
        <InnerBlock key={i} tint={activeTint} heading={s.heading} subheading={s.subheading}>
          <BulletList items={s.items} />
        </InnerBlock>
      );
    case "numbered":
      return (
        <InnerBlock key={i} tint={activeTint} heading={s.heading} subheading={s.subheading}>
          <NumberedList items={s.items} />
        </InnerBlock>
      );
    case "process":
      return (
        <InnerBlock key={i} tint={s.tint || tint} heading={s.heading || "How we work"} subheading={s.subheading}>
          <NumberedList items={s.items || STANDARD_PROCESS} />
        </InnerBlock>
      );
    case "stack":
      return (
        <InnerBlock key={i} tint={s.tint || tint} heading={s.heading} subheading={s.subheading}>
          <div className="flex flex-wrap gap-2">
            {s.items.map((t, k) => (
              <span key={k} className="rounded-full border border-wafa-deep/15 bg-white px-3 py-1.5 text-xs font-medium text-wafa-deep">
                {t}
              </span>
            ))}
          </div>
        </InnerBlock>
      );
    case "faq":
      return <FaqSection key={i} heading={s.heading} items={s.items} />;
    case "placeholder":
      return (
        <InnerBlock key={i} tint={s.tint || tint} heading={s.heading}>
          <Placeholder id={s.id} width={s.width} height={s.height} />
          {s.caption ? <div className="mt-3 text-xs text-wafa-ink/60">{s.caption}</div> : null}
        </InnerBlock>
      );
    default:
      return null;
  }
}
